import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import {SpeechBubbleIcon} from './svg';

import {getCommentsForQuestion} from '../api/api';
import {CommentEditor} from './CommentEditor';
import Comment from './Comment';
import ExpertAnswer from './ExpertAnswer';
import {QAPage} from './LinkedData';
import DropdownInput from './DropdownInput';
import {useAppContext, useAnalytics} from '../hooks';


const renderMetadata = (question, comments) => {
  if (!question || !comments || !comments.length) return null;

  const answers = comments.map((c) => ({
    text: c.data.text,
    upvotes: c.voters ? c.voters.length : 0,
    url: `/questions/${question.id}/${question.slug}#${c.id}`,
  }));

  return (
    <QAPage
      question={question.data.title}
      questionText={question.data.title}
      acceptedAnswer={answers[0]}
      answers={answers.slice(1, 5)}
      upvoteCount={question.votes}
      dateCreated={question.createdAt}
    />
  );
};

const SortTypes = {
  HOT: 'hot',
  TOP: 'top',
  NEW: 'new',
};

const SortOptions = [{
  label: '🔥 Hot',
  value: SortTypes.HOT,
}, {
  label: '⬆️ Top',
  value: SortTypes.TOP,
}, {
  label: '✨ New',
  value: SortTypes.NEW,
}];

const QuestionCommentSection = ({question, onTriggerSignup, isGated}) => {
  const [commentData, setCommentData] = useState({root: []});
  const [sort, setSort] = useState(SortTypes.HOT);
  const [expertAnswer, setExpertAnswer] = useState(null);
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  const onSubmit = () => {
    tracker.questionSubmitComment();
    window.location.reload();
  };

  const onOpen = () => {
    tracker.questionStartComment();
  };

  const getHotnessScore = (comment) => {
    const noOfReplies = (comment.id in commentData) ? commentData[comment.id].length : 0;
    const age = moment().diff(moment(comment.created_at, 'YYYY-MM-DD'), 'days');
    const hotness = (
      1 * comment.voters.length
          + 1 * noOfReplies
          + 0.01 * comment.karma)
          / (age + 1) ** 1.8;

    return hotness;
  };

  const sortComments = (commentsToSort, sortMethod = SortTypes.HOT) => {
    switch (sortMethod) {
      case SortTypes.TOP:
        // Sort by voters. If even, use karma to break tie
        return commentsToSort.slice().sort((a, b) => (
          b.voters.length - a.voters.length || b.karma - a.karma));
      case SortTypes.NEW:
        return commentsToSort.slice().sort((a, b) => moment(b.created_at) - moment(a.created_at));
      default:
        // Hot by default
        return commentsToSort.slice().sort((a, b) => ((a.hotness < b.hotness) ? 1 : -1));
    }
  };

  const handleSortChange = (sortMethod) => {
    setSort(sortMethod);
    const sortedComments = sortComments(commentData.root, sortMethod);
    setCommentData({...commentData, root: sortedComments});
    tracker.questionCommentSort({sort: sortMethod});
  };

  useEffect(() => {
    if (question) {
      getCommentsForQuestion(question.id).then((data) => {
        setExpertAnswer(_.remove(data.root, 'data.expert')[0]);

        /* eslint-disable no-param-reassign */
        data.root.forEach((comment) => { comment.hotness = getHotnessScore(comment); });
        data.root = sortComments(data.root);
        /* eslint-enable no-param-reassign */

        setCommentData(data);
      });
    }
  }, [question?.id]);

  const comments = commentData.root;

  return question && (
    <div className="w-full mb-12">
      {(currentUser && !isGated) ? (
        <CommentEditor
          questionID={question.id}
          onOpen={onOpen}
          onSubmit={onSubmit}
          className="mb-12"
        />
      ) : (
        <button
          className="font-medium text-center border border-gray-200 rounded-lg w-full h-20 mb-6 hover:shadow"
          onClick={onTriggerSignup}
          type="button"
        >
          {currentUser
            ? 'Become a member to post an answer to this question.'
            : 'Log in or sign up to answer this question.'}
        </button>
      )}
      {(comments.length > 0 || expertAnswer) && (
        <>
          <div className="mb-2 flex justify-between">
            <div className="flex text-gray-500 text-sm font-medium items-center">
              <SpeechBubbleIcon stroke="currentColor" className="text-gray-500 h-5 w-5 mr-1.5" />
              {comments.length}
            </div>
            <DropdownInput
              value={sort}
              options={SortOptions}
              onChange={handleSortChange}
              showReset={false}
              highlightActive={false}
              align="right"
            />
          </div>
          <div id="answers">
            {expertAnswer && (
            <ExpertAnswer
              comment={expertAnswer}
              className="mb-6"
              onView={() => tracker.questionViewExpertAnswer({id: question.id})}
              onClickUpgrade={() => tracker.questionClickExpertAnswerUpgrade({id: question.id})}
            />
            )}
            {comments.map((c) => (
              <Comment
                onVote={isGated ? onTriggerSignup : undefined}
                questionID={question.id}
                data={c}
                replies={commentData[c.id]}
                isGated={isGated}
                onTriggerSignup={onTriggerSignup}
                key={c.id}
              />
            ))}
          </div>
        </>
      )}
      {comments.length > 0 && renderMetadata(question, comments)}
    </div>
  );
};

QuestionCommentSection.propTypes = {
  question: PropTypes.shape().isRequired,
  isGated: PropTypes.bool.isRequired,
  onTriggerSignup: PropTypes.func.isRequired,
};

export default QuestionCommentSection;
